import React, { useState } from 'react';
import { motion } from 'framer-motion';

const TECH_CATEGORIES = [
  {
    id: 'frontend',
    label: 'Frontend',
    icon: '🎨',
    color: '#61dafb',
    skills: [
      { name: 'React', level: 90 },
      { name: 'TypeScript', level: 75 },
      { name: 'Framer Motion', level: 80 },
      { name: 'Chakra UI', level: 85 },
      { name: 'Three.js', level: 60 },
    ],
  },
  {
    id: 'backend',
    label: 'Backend',
    icon: '⚙️',
    color: '#68d391',
    skills: [
      { name: 'Node.js', level: 90 },
      { name: 'Express', level: 88 },
      { name: 'REST APIs', level: 92 },
      { name: 'JWT Auth', level: 85 },
      { name: 'WebSockets', level: 75 },
    ],
  },
  {
    id: 'database',
    label: 'Bases de datos',
    icon: '🗄️',
    color: '#f6ad55',
    skills: [
      { name: 'MongoDB', level: 85 },
      { name: 'PostgreSQL', level: 70 },
      { name: 'Mongoose', level: 88 },
      { name: 'Redis', level: 50 },
    ],
  },
  {
    id: 'ai',
    label: 'IA & Agentes',
    icon: '🧠',
    color: '#b794f4',
    skills: [
      { name: 'Groq / LLaMA', level: 85 },
      { name: 'Prompt Engineering', level: 88 },
      { name: 'RAG', level: 70 },
      { name: 'Agentes autónomos', level: 75 },
    ],
  },
  {
    id: 'iot',
    label: 'IoT & Hardware',
    icon: '🔌',
    color: '#fc8181',
    skills: [
      { name: 'ESP32 / Arduino', level: 90 },
      { name: 'C++', level: 75 },
      { name: 'MQTT', level: 80 },
      { name: 'Sensores', level: 88 },
    ],
  },
  {
    id: 'devops',
    label: 'DevOps & Cloud',
    icon: '☁️',
    color: '#63b3ed',
    skills: [
      { name: 'Docker', level: 80 },
      { name: 'Railway / Render', level: 85 },
      { name: 'Vercel', level: 88 },
      { name: 'Git / GitHub', level: 92 },
    ],
  },
];

const SkillBar = ({ name, level, color, index }) => (
  <motion.div
    initial={{ opacity: 0, x: -20 }}
    whileInView={{ opacity: 1, x: 0 }}
    viewport={{ once: true }}
    transition={{ duration: 0.4, delay: index * 0.06 }}
    style={{ marginBottom: '0.9rem' }}
  >
    <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '0.3rem' }}>
      <span style={{ color: 'rgba(242,242,242,0.8)', fontSize: '0.82rem', fontWeight: 500 }}>{name}</span>
      <span style={{ color: color, fontSize: '0.75rem', fontWeight: 700 }}>{level}%</span>
    </div>
    <div style={{
      height: '4px', background: 'rgba(255,255,255,0.06)',
      borderRadius: '4px', overflow: 'hidden',
    }}>
      <motion.div
        initial={{ width: 0 }}
        whileInView={{ width: `${level}%` }}
        viewport={{ once: true }}
        transition={{ duration: 1, ease: 'easeOut', delay: index * 0.06 + 0.2 }}
        style={{ height: '100%', background: color, borderRadius: '4px',
          boxShadow: `0 0 8px ${color}60` }}
      />
    </div>
  </motion.div>
);

const CategoryCard = ({ cat, isActive, onClick }) => (
  <motion.button
    onClick={() => onClick(cat.id)}
    whileHover={{ scale: 1.04 }}
    whileTap={{ scale: 0.97 }}
    style={{
      display: 'flex', alignItems: 'center', gap: '0.5rem',
      padding: '0.5rem 1rem', borderRadius: '24px',
      background: isActive ? `${cat.color}18` : 'rgba(255,255,255,0.03)',
      border: `1px solid ${isActive ? cat.color + '60' : 'rgba(255,255,255,0.07)'}`,
      color: isActive ? cat.color : 'rgba(242,242,242,0.5)',
      fontSize: '0.82rem', fontWeight: isActive ? 700 : 500,
      cursor: 'pointer', transition: 'all 0.2s',
    }}
  >
    <span>{cat.icon}</span>
    <span>{cat.label}</span>
  </motion.button>
);

const TOOLS = [
  'VS Code', 'Postman', 'Figma', 'GitHub', 'Docker Desktop',
  'PlatformIO', 'Arduino IDE', 'Railway', 'Cloudinary', 'Vercel',
];

const Skills = () => {
  const [activeId, setActiveId] = useState('frontend');
  const activeCat = TECH_CATEGORIES.find((c) => c.id === activeId);

  return (
    <section id="skills" style={{ background: '#080808', padding: '6rem 0' }}>
      <style>{`
        @keyframes float-chip {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-5px); }
        }
      `}</style>

      <div style={{ maxWidth: '1100px', margin: '0 auto', padding: '0 1.5rem' }}>

        {/* Section header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center', marginBottom: '3.5rem' }}
        >
          <p style={{ color: '#ff4500', fontSize: '0.8rem', letterSpacing: '3px', textTransform: 'uppercase', marginBottom: '0.75rem' }}>
            Arsenal técnico
          </p>
          <h2 style={{ fontSize: 'clamp(2rem, 5vw, 3rem)', color: '#f2f2f2', fontWeight: 800, marginBottom: '1rem' }}>
            Stack completo,
            <span style={{ color: '#ff4500' }}> soluciones reales</span>
          </h2>
          <p style={{ color: 'rgba(242,242,242,0.45)', maxWidth: '520px', margin: '0 auto', lineHeight: 1.8, fontSize: '0.93rem' }}>
            De la interfaz de usuario al firmware de un microcontrolador —
            manejo el stack completo para construir productos que funcionan de verdad.
          </p>
        </motion.div>

        {/* Category tabs */}
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.6rem', justifyContent: 'center', marginBottom: '2.5rem' }}>
          {TECH_CATEGORIES.map((cat) => (
            <CategoryCard
              key={cat.id}
              cat={cat}
              isActive={activeId === cat.id}
              onClick={setActiveId}
            />
          ))}
        </div>

        {/* Active category panel */}
        <motion.div
          key={activeId}
          initial={{ opacity: 0, y: 16 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35 }}
          style={{
            background: `linear-gradient(135deg, ${activeCat.color}08 0%, rgba(255,255,255,0.01) 100%)`,
            border: `1px solid ${activeCat.color}25`,
            borderRadius: '20px', padding: '2.5rem',
            marginBottom: '3rem',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '1.75rem' }}>
            <span style={{ fontSize: '2rem' }}>{activeCat.icon}</span>
            <div>
              <h3 style={{ color: activeCat.color, fontWeight: 800, fontSize: '1.1rem', marginBottom: '0' }}>{activeCat.label}</h3>
              <p style={{ color: 'rgba(242,242,242,0.4)', fontSize: '0.78rem' }}>
                {activeCat.skills.length} tecnologías
              </p>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '0 3rem' }}>
            {activeCat.skills.map((skill, i) => (
              <SkillBar
                key={skill.name}
                name={skill.name}
                level={skill.level}
                color={activeCat.color}
                index={i}
              />
            ))}
          </div>
        </motion.div>

        {/* All skills quick overview */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ marginBottom: '3rem' }}
        >
          <p style={{ color: 'rgba(242,242,242,0.35)', fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', textAlign: 'center', marginBottom: '1.5rem' }}>
            Todas las categorías
          </p>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: '0.75rem' }}>
            {TECH_CATEGORIES.map((cat, ci) => (
              <motion.div
                key={cat.id}
                whileHover={{ scale: 1.03, borderColor: `${cat.color}50` }}
                onClick={() => setActiveId(cat.id)}
                style={{
                  background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)',
                  borderRadius: '12px', padding: '1rem', cursor: 'pointer',
                  transition: 'all 0.2s',
                }}
              >
                <div style={{ fontSize: '1.4rem', marginBottom: '0.4rem' }}>{cat.icon}</div>
                <div style={{ color: cat.color, fontSize: '0.72rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '0.5rem' }}>
                  {cat.label}
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.3rem' }}>
                  {cat.skills.slice(0, 3).map((s) => (
                    <span key={s.name} style={{
                      fontSize: '0.68rem', padding: '0.15rem 0.45rem',
                      background: `${cat.color}12`, border: `1px solid ${cat.color}25`,
                      color: 'rgba(242,242,242,0.55)', borderRadius: '20px',
                    }}>{s.name}</span>
                  ))}
                  {cat.skills.length > 3 && (
                    <span style={{ fontSize: '0.68rem', color: 'rgba(242,242,242,0.3)' }}>+{cat.skills.length - 3}</span>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* Tools belt */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ textAlign: 'center' }}
        >
          <p style={{ color: 'rgba(242,242,242,0.3)', fontSize: '0.75rem', letterSpacing: '2px', textTransform: 'uppercase', marginBottom: '1rem' }}>
            Herramientas del día a día
          </p>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', justifyContent: 'center' }}>
            {TOOLS.map((tool, i) => (
              <motion.span
                key={tool}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.05 }}
                style={{
                  padding: '0.3rem 0.8rem', borderRadius: '20px',
                  background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.07)',
                  color: 'rgba(242,242,242,0.5)', fontSize: '0.78rem',
                  animation: `float-chip ${2.5 + i * 0.3}s ease-in-out infinite`,
                }}
              >
                {tool}
              </motion.span>
            ))}
          </div>
        </motion.div>

      </div>
    </section>
  );
};

export default Skills;
